import api from './client';
import { ApprovalRequest, ApprovalRequestsResponse, ApprovalStatus, CutApprovalsResponse } from '../types';

export interface CreateApprovalRequestParams {
  commentId: string;
  targetUserId?: string;
}

export interface RespondToApprovalParams {
  status: 'APPROVED' | 'REJECTED';
  responseNote?: string;
}

export interface ListApprovalsParams {
  status?: ApprovalStatus;
  role?: 'requester' | 'target';
  cutId?: string;
  limit?: number;
  offset?: number;
}

/**
 * Create an approval request for a comment
 */
export async function createApprovalRequest(
  params: CreateApprovalRequestParams
): Promise<ApprovalRequest> {
  const response = await api.post<ApprovalRequest>('/approvals', params);
  return response.data;
}

/**
 * List approval requests with optional filters
 */
export async function listApprovalRequests(
  params?: ListApprovalsParams
): Promise<ApprovalRequestsResponse> {
  const response = await api.get<ApprovalRequestsResponse>('/approvals', { params });
  return response.data;
}

/**
 * Get the count of pending approval requests for the current user
 */
export async function getPendingApprovalCount(): Promise<{ count: number }> {
  const response = await api.get<{ count: number }>('/approvals/pending/count');
  return response.data;
}

/**
 * Get a single approval request by ID
 */
export async function getApprovalRequest(id: string): Promise<ApprovalRequest> {
  const response = await api.get<ApprovalRequest>(`/approvals/${id}`);
  return response.data;
}

/**
 * Respond to an approval request (approve or reject)
 */
export async function respondToApprovalRequest(
  id: string,
  params: RespondToApprovalParams
): Promise<ApprovalRequest> {
  const response = await api.patch<ApprovalRequest>(`/approvals/${id}/respond`, params);
  return response.data;
}

/**
 * Cancel an approval request (only by requester)
 */
export async function cancelApprovalRequest(id: string): Promise<{ message: string }> {
  const response = await api.delete<{ message: string }>(`/approvals/${id}`);
  return response.data;
}

/**
 * Get all approval requests for a specific cut, grouped by audio file
 */
export async function getCutApprovals(cutId: string): Promise<CutApprovalsResponse> {
  const response = await api.get<CutApprovalsResponse>(`/approvals/cut/${cutId}`);
  return response.data;
}
